import React, { useState, useEffect, useContext } from "react";
import { useAuth } from "../context/auth";
import { authContextProps } from "../@interfaces/authContext";
import {
  get_folders,
  checkStaff,
} from "../networking/sequenceControllerNetwork";
import styled from "styled-components";
import FolderLinkItem from "../components/FolderLinkItem";
import { FaRegFolder } from "react-icons/fa";
import { Context } from "../context/context";
import { ContextProps } from "../@interfaces/interfaces";
import { FolderCallProps } from "../@interfaces/other";
import Spinner from "../components/Spinner";

const Wrapper = styled.div`
  height: 100vh;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-direction: column;
  background-color: #f8f8f8;
  overflow-y: auto;
  padding: 2em;

  .box--container {
    flex-shrink: 0;
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    background-color: inherit;
    justify-content: center;
  }

  @media (max-width: 1300px) {
    .box--container {
      align-items: center;
      gap: 0;
      flex-direction: column;
    }
  }

  .main--box {
    margin-top: 2em;
    border: 1px solid #ccc;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    flex-direction: column;

    flex-shrink: 0;
    width: 600px;
    @media (max-width: 600px) {
      width: 100%;
    }
  }

  .text--box {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
  }

  .text--main {
    img {
      background-color: inherit;
      margin-right: 0.1em;
    }
    text-align: center;
    font-size: 1.85rem;
    font-weight: 700;
    color: var(--ui-text);
    background-color: inherit;
    flex-shrink: 0;
    width: min(1220px, 100%);
    margin-top: 0.25rem;
    margin-bottom: 0.35rem;

    @media (max-width: 700px) {
      font-size: 1.45rem;
      text-align: center;
    }
  }

  .span--accent {
    background-color: inherit;
  }

  .text--folder {
    background-color: var(--ui-accent);
    color: var(--ui-day);
    margin-bottom: 0;
    width: 100%;
    padding: 0.55rem 0.8rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid rgba(255, 255, 255, 0.18);
  }

  .text--folder--left {
    background-color: inherit;
    display: flex;
    align-items: center;
    gap: 0.35rem;
    min-width: 0;
  }

  .text--folder--title {
    background-color: inherit;
    font-size: 1rem;
    font-weight: 650;
    letter-spacing: 0.01em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .text--folder--count {
    background-color: inherit;
    font-size: 0.74rem;
    font-weight: 600;
    margin-left: 0.5rem;
    opacity: 0.95;
    flex-shrink: 0;
  }

  .text--folder--icon {
    background-color: inherit;
    @media (max-width: 600px) {
      font-size: 0.8em;
    }
  }

  @media (max-width: 600px) {
    .text--folder {
      padding: 0.55rem 0.7rem;
    }

    .text--folder--title {
      font-size: 0.93rem;
    }

    .text--folder--count {
      font-size: 0.7rem;
      margin-left: 0.35rem;
    }
  }

  .folders {
    display: flex;
    flex-direction: column;
    width: 100%;
    box-sizing: border-box;
    overflow: auto;
    max-height: calc(100vh - 300px);
  }

  .folder__empty {
    font-weight: 500;
    margin-top: 1em;
  }
`;

interface Folder {
  name: string;
  url: string;
  time: Date;
  user: string;
  status?: string;
  review_status?: "pending_review" | "changes_requested" | "accepted";
  completion_percentage?: number;
}

function HomePage() {
  const { token, user } = useAuth() as authContextProps;
  const [isStaff, setIsStaff] = useState<boolean>(false); // user.user_type === "staff" ? true : false
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { clearImages, clearSelections } = useContext(Context) as ContextProps;

  const [folders, setFolders] = useState<Folder[]>([]);
  const [foldersMarked, setFoldersMarked] = useState<Folder[]>([]);
  const [foldersRejected, setFoldersRejected] = useState<Folder[]>([]);

  document.title = "Stribor";
  useEffect(() => {
    setIsLoading(true);
    clearImages();
    clearSelections();
    get_folders(token).then((res) => {
      if (res.success) {
        setFolders(
          res.data.map((el: FolderCallProps) => {
            return {
              name: el.name,
              url: el.url,
              time: new Date(el.time),
              user: el.user,
              completion_percentage: el.completion_percentage,
            };
          }),
        );

        if (res.dataStaff) {
          setFoldersMarked(
            res.dataStaff.map((el: FolderCallProps) => {
              return {
                name: el.name,
                url: el.url,
                time: new Date(el.time),
                user: el.user,
                status: el.status,
                review_status: el.review_status,
                completion_percentage: el.completion_percentage,
              };
            }),
          );
        }

        if (res.dataRejected) {
          setFoldersRejected(
            res.dataRejected.map((el: FolderCallProps) => {
              return {
                name: el.name,
                url: el.url,
                time: new Date(el.time),
                user: el.user,
                status: el.status,
                review_status: el.review_status,
                completion_percentage: el.completion_percentage,
              };
            }),
          );
        }
      }
      checkStaff(token).then((res) => {
        if (res && "data" in res && res.data?.is_staff) {
          setIsStaff(true);
        } else {
          setIsStaff(false);
        }
      });
      setIsLoading(false);
    });
  }, [clearImages, clearSelections, token]);

  return (
    <Wrapper>
      {isLoading && <Spinner />}
      {!isLoading && (
        <>
          <div className="text--main">
            Welcome <span className="span--accent">{user.username}!</span>
          </div>
          {/* Main labeling box */}
          <div className="box--container">
            <div className="main--box">
              <div className="text--box">
                <div className="text--folder">
                  <div className="text--folder--left">
                    <FaRegFolder className="text--folder--icon" />
                    <span className="text--folder--title">To Label</span>
                  </div>
                  <span className="text--folder--count">
                    {folders.length} items left
                  </span>
                </div>
              </div>
              {folders.length === 0 && (
                <div className="folder__empty">No sequences to label</div>
              )}
              <div className="folders">
                {folders
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((folder, i) => {
                    const currTime = Math.floor(new Date().getTime() / 1000.0);
                    const timeDiff =
                      folder.time.getTime() &&
                      currTime - Math.floor(folder.time.getTime() / 1000.0) <
                        7200
                        ? currTime - Math.floor(folder.time.getTime() / 1000.0)
                        : -1;
                    return (
                      <FolderLinkItem
                        key={i}
                        isAdmin={isStaff}
                        folder_group="neoznacene"
                        folder={folder.name}
                        lock_timer={timeDiff}
                        userThatMadeSeq={folder.user}
                        completionPercentage={folder.completion_percentage}
                      />
                    );
                  })}
              </div>
            </div>

            {/* Error labeling box */}
            <div className="main--box">
              <div className="text--box">
                <div className="text--folder">
                  <div className="text--folder--left">
                    <FaRegFolder className="text--folder--icon" />
                    <span className="text--folder--title">Needs Fixes</span>
                  </div>
                  <span className="text--folder--count">
                    {foldersRejected.length} items
                  </span>
                </div>
              </div>
              {foldersRejected.length === 0 && (
                <div className="folder__empty">No sequences to fix</div>
              )}
              <div className="folders">
                {foldersRejected
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((folder, i) => {
                    return (
                      <FolderLinkItem
                        key={i}
                        folder_group="rejected"
                        folder={folder.name}
                        reviewed={
                          folder.review_status === "accepted" ||
                          folder.status === "marked"
                        }
                        userThatMadeSeq={folder.user}
                        completionPercentage={folder.completion_percentage}
                      />
                    );
                  })}
              </div>
            </div>

            {/* Admin labeling box */}
            {isStaff && (
              <div className="main--box">
                <div className="text--box">
                  <div className="text--folder">
                    <div className="text--folder--left">
                      <FaRegFolder className="text--folder--icon" />
                      <span className="text--folder--title">For Review</span>
                    </div>
                    <span className="text--folder--count">
                      {foldersMarked.length} items
                    </span>
                  </div>
                </div>
                {foldersMarked.length === 0 && (
                  <div className="folder__empty">No sequences to review</div>
                )}
                <div className="folders">
                  {foldersMarked
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((folder, i) => {
                      return (
                        <FolderLinkItem
                          key={i}
                          folder_group="oznacene"
                          folder={folder.name}
                          reviewed={
                            folder.review_status === "accepted" ||
                            folder.status === "marked"
                          }
                          userThatMadeSeq={folder.user}
                        />
                      );
                    })}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </Wrapper>
  );
}

export default HomePage;
